import { useEffect, useRef, useState } from "react";
import abcjs from "abcjs";
import { Gauge, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { AbcNotation } from "./AbcNotation";
import { FullscreenOverlay } from "./FullscreenOverlay";
import { cleanAbc } from "../lib/abcUtils";

const MIN = 20;
const MAX = 220;

export const AbcPlayer = ({ abc, showNotation = false, tuneName = "", zoom = 1 }) => {
  const detachedRef = useRef(null);
  const controllerRef = useRef(null);
  const loadedRef = useRef(false);
  const readyRef = useRef(false);
  const controlIdRef = useRef(`abc-audio-${Math.random().toString(36).slice(2)}`);
  const [speed, setSpeed] = useState(100);
  const [supported, setSupported] = useState(true);
  const [full, setFull] = useState(false);

  // create + load the synth controller once (guarded against StrictMode double-invoke)
  useEffect(() => {
    if (loadedRef.current) return;
    if (!abcjs.synth.supportsAudio()) {
      setSupported(false);
      return;
    }
    const el = document.getElementById(controlIdRef.current);
    if (!el) return;
    const controller = new abcjs.synth.SynthController();
    controller.load(`#${controlIdRef.current}`, null, {
      displayPlay: true,
      displayProgress: true,
      displayWarp: true,
      displayClock: false,
    });
    controllerRef.current = controller;
    loadedRef.current = true;
  }, []);

  // feed the synth from a fully detached node (zero layout impact)
  useEffect(() => {
    if (!supported || !loadedRef.current || !abc) return;
    let cancelled = false;
    readyRef.current = false;
    if (!detachedRef.current) detachedRef.current = document.createElement("div");
    try {
      const visualObj = abcjs.renderAbc(detachedRef.current, cleanAbc(abc), {})[0];
      controllerRef.current
        .setTune(visualObj, false, {})
        .then(() => {
          if (cancelled) return;
          readyRef.current = true;
          safeSetWarp(speed);
        })
        .catch(() => {});
    } catch (e) {
      /* ignore malformed intermediate ABC */
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abc, supported]);

  const safeSetWarp = (v) => {
    try {
      if (controllerRef.current && readyRef.current) controllerRef.current.setWarp(v);
    } catch (e) {
      /* warp element not ready yet */
    }
  };

  const changeSpeed = (v) => {
    setSpeed(v);
    safeSetWarp(v);
  };

  if (!supported) {
    return (
      <div
        className="flex items-center gap-2 rounded-2xl bg-[#f3efea] px-4 py-3 text-sm text-[#6e655b]"
        data-testid="audio-unsupported"
      >
        <VolumeX className="h-4 w-4" /> Audio playback is not supported in this browser.
      </div>
    );
  }

  return (
    <div
      className="rounded-3xl border border-[#e5e0d8] bg-[#fffdf9] p-4 shadow-[0_8px_30px_rgba(45,40,36,0.05)]"
      data-testid="abc-player"
    >
      {/* notation lives inside the player, with a fullscreen button */}
      {showNotation && (
        <div
          className="relative mb-4 overflow-auto rounded-2xl border border-[#efe9df] bg-white p-4"
          data-testid="live-notation"
        >
          <button
            onClick={() => setFull(true)}
            className="absolute right-2 top-2 z-10 rounded-full border border-[#e5e0d8] bg-white/90 p-2 text-[#2d2824] shadow-sm hover:bg-[#f3efea]"
            data-testid="notation-fullscreen"
            aria-label="fullscreen sheet music"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <div style={{ width: `${100 / zoom}%`, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
            <AbcNotation abc={abc} respectBreaks />
          </div>
        </div>
      )}

      <FullscreenOverlay open={full} onClose={() => setFull(false)} title={tuneName || "Sheet music"}>
        {/* reflow to the real overlay width: user line breaks would overflow on phones */}
        <div className="mx-auto w-full max-w-6xl">
          <AbcNotation abc={abc} fit />
        </div>
      </FullscreenOverlay>

      <div className="mb-3 flex items-center gap-2">
        <Volume2 className="h-4 w-4 text-[#2d2824]" />
        <span className="text-xs font-extrabold uppercase tracking-widest text-[#2d2824]">
          Live player
        </span>
      </div>

      {/* abcjs play + progress (Foinn.org palette, black icons/text) */}
      <div id={controlIdRef.current} data-testid="audio-controls" className="abc-player-foinn" />

      {/* tempo */}
      <div className="mt-4 flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f3efea]">
          <Gauge className="h-5 w-5 text-[#2d2824]" />
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-[#2d2824]">Tempo</span>
            <span className="font-serif text-lg font-bold text-[#2d2824]" data-testid="speed-value">
              {speed}%
            </span>
          </div>
          <input
            type="range"
            min={MIN}
            max={MAX}
            step={5}
            value={speed}
            onChange={(e) => changeSpeed(Number(e.target.value))}
            className="fame-range mt-1"
            style={{ "--pct": `${((speed - MIN) / (MAX - MIN)) * 100}%`, "--c": "#2d2824" }}
            data-testid="speed-slider"
          />
          <div className="mt-1 flex justify-between text-[10px] font-semibold text-[#6e655b]">
            <span>{MIN}%</span>
            <span>100%</span>
            <span>{MAX}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};
